// Three anchoring looks of mockup round 056 (R1125, `ui-map-interface.md`
// §6.1.5): cards 15 GROUND LINE, 16 BUBBLE COLUMN and 20 RING TEETH: the looks
// that stand on the floor under the hull, and the ring with teeth.
//
// One entry per look id of `labels.toml anchor_styles` that this file owns.
// `RECIPES` holds the built cards and `PENDING` the ones still to build; the
// two lists are disjoint from those of every other recipe file, and
// `anchor-styles.test.ts` pins that. A pending look draws the AUTO look.
//
// Every FIGURE stands in the look's row of `data/presentation/anchor.toml`.
// A constant here is a PROPORTION of a figure the row carries: the quotient
// says what the card wrote, and it holds at any figure (R1120).

import {
  DEEP_INK, TAU, TWELVE, WATER_INK, arcOf, bezier2, disc, easeOut, fillAlpha, line, ring, span,
  type AnchorCtx, type AnchorRecipe,
} from './anchor-recipe-kit';
import { GATHER_WEDGE_LEN, gatherCellKey } from './gather-recipe-kit';
import { mulberry32 } from '../world';
import type { AtlasSpec } from '../fast/gfx/atlas';

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

// The card's `DEEP`, the dark core of a hollow pip, is the combat kit's
// `DEEP_INK`: the chrome `--deep` of the palette.

/**
 * A filled triangle with its apex at `(x, y)`, pointing along `ang`, `len`
 * long and `hw` wide each side of its axis. It is the gather kit's `wedge`
 * cell (R1120), the one filled triangle the atlas bakes, tinted to the
 * cable ink.
 */
function wedge(
  c: AnchorCtx, x: number, y: number, ang: number,
  len: number, hw: number, ink: number, a: number,
): void {
  const al = fillAlpha(a);
  if (len <= 0 || hw <= 0 || al <= 0) return;
  const e = c.sprite(gatherCellKey('wedge'));
  const sx = len / GATHER_WEDGE_LEN, sy = 2 * hw;
  c.quads.quad(
    x, y, e.ox * sx, e.oy * sy, e.w * sx, e.h * sy, ang, 0, ink, al,
    e.u0, e.v0, e.u1, e.v1,
  );
}

/** How many straight pieces one quadratic curve of a card becomes. */
const CURVE_STEPS = 6;

/**
 * One quadratic curve of a card as `CURVE_STEPS` strokes, with the card's
 * round caps and joins. Every point is `(ox + u * s, oy + v * s)` of the
 * card's own `(u, v)`.
 */
function curve(
  c: AnchorCtx, ox: number, oy: number, s: number,
  u0: number, v0: number, cu: number, cv: number, u1: number, v1: number,
  w: number, ink: number, a: number,
): void {
  let px = ox + u0 * s, py = oy + v0 * s;
  for (let i = 1; i <= CURVE_STEPS; i++) {
    const t = i / CURVE_STEPS;
    const nx = ox + bezier2(u0, cu, u1, t) * s, ny = oy + bezier2(v0, cv, v1, t) * s;
    line(c, px, py, nx, ny, w, ink, a);
    px = nx; py = ny;
  }
}

// ---------------------------------------------------------------------------
// Card 15, GROUND LINE
// ---------------------------------------------------------------------------

/** The first hatch stroke's place right of the bar's west end, and the
 *  stroke's top, run west and foot, as shares of the hatch step (the card's
 *  4, 1.4, 3.6 and 5.8 against 4.4). */
const HATCH_X0_K = 4 / 4.4;
const HATCH_TOP_K = 1.4 / 4.4;
const HATCH_RUN_K = 3.6 / 4.4;
const HATCH_FOOT_K = 5.8 / 4.4;
/** The half height of an end stop, as a share of the pip's half height
 *  (2 against 3.4), and its stroke, as a share of the hatch stroke (1
 *  against 1.2). */
const STOP_HALF_K = 2 / 3.4;
const STOP_W_K = 1 / 1.2;
/** The pip fluke, as shares of the pip length (the card's 5): it hangs 1
 *  under the bar, its shank is 5 long, and its curve runs from 3 each side
 *  at 2.4 down through a control point 7.2 down. */
const FLUKE_DROP_K = 1 / 5;
const FLUKE_HALF_K = 3 / 5;
const FLUKE_ARM_K = 2.4 / 5;
const FLUKE_CTRL_K = 7.2 / 5;
/** The pip fluke's stroke and the hollow pip's outline, as shares of the
 *  hatch stroke (1.1 against 1.2). */
const PIP_W_K = 1.1 / 1.2;
/** How far east of the fill end the hollow WEIGHING pip's apex stands, as a
 *  share of the pip length (0.5 against 5). */
const PIP_BACK_K = 0.5 / 5;

/**
 * A bar under the hull that does not turn with the heading.
 *
 * SETTING: a faint track with end stops fills from the west with the share of
 * cable out, and a solid pip that points east rides the end of the fill,
 * with a small fluke under it. SET: the bar is the engineering symbol of a
 * fixed support, a solid line with hatch strokes under it that land one by
 * one over `flash_sec`. WEIGHING: the fill drains, the hatch is gone, and the
 * pip is hollow and points west. At a zoom under 1 the whole mark grows by
 * `grow` about the bar's middle, so the pip keeps its form.
 */
function groundLine(c: AnchorCtx): void {
  const r = c.row, ink = c.ink, a = c.a;
  const g = c.z < 1 ? r.grow : 1;
  const y = c.y + r.yPx;
  const x0 = c.x - r.rPx * g, x1 = c.x + r.rPx * g;
  if (c.phase === 'set') {
    line(c, x0, y, x1, y, r.widthPx * g, ink, a, false);
    const k = span(c.since, 0, r.flashSec);
    const step = r.length3Px;
    for (let i = 0; i < r.count; i++) {
      if (i / r.count > k) break;
      const hx = x0 + (HATCH_X0_K * step + i * step) * g;
      line(c, hx, y + HATCH_TOP_K * step * g, hx - HATCH_RUN_K * step * g,
        y + HATCH_FOOT_K * step * g, r.width3Px * g, ink, r.alpha4 * a);
    }
    return;
  }
  const xf = x0 + 2 * r.rPx * c.p * g;
  line(c, x0, y, x1, y, r.width2Px * g, ink, r.alpha2 * a, false);
  if (c.p > 0) line(c, x0, y, xf, y, r.width2Px * g, ink, r.alpha * a, false);
  // The end stops, so an empty track still reads as a bar.
  const sh = r.length2Px * STOP_HALF_K * g, sw = r.width3Px * STOP_W_K * g;
  line(c, x0, y - sh, x0, y + sh, sw, ink, r.alpha3 * a);
  line(c, x1, y - sh, x1, y + sh, sw, ink, r.alpha3 * a);
  // The pip fluke: a shank down from the fill end and a curve across it.
  const L = r.lengthPx, pw = r.width3Px * PIP_W_K * g;
  const fy = y + L * FLUKE_DROP_K * g;
  line(c, xf, fy, xf, fy + L * g, pw, ink, r.alpha4 * a);
  curve(c, xf, fy, L * g, -FLUKE_HALF_K, FLUKE_ARM_K, 0, FLUKE_CTRL_K,
    FLUKE_HALF_K, FLUKE_ARM_K, pw, ink, r.alpha4 * a);
  // The pip: solid and east in SETTING, hollow and west in WEIGHING.
  const len = r.lengthPx * g, hw = r.length2Px * g;
  if (c.phase === 'setting') {
    wedge(c, xf + len, y, 0, len, hw, ink, a);
    return;
  }
  const ax = xf + r.lengthPx * PIP_BACK_K * g, bx = ax + len;
  wedge(c, ax, y, Math.PI, len, hw, DEEP_INK, r.alpha4);
  line(c, bx, y - hw, ax, y, pw, ink, a);
  line(c, ax, y, bx, y + hw, pw, ink, a);
  line(c, bx, y + hw, bx, y - hw, pw, ink, a);
}

// ---------------------------------------------------------------------------
// Card 16, BUBBLE COLUMN
// ---------------------------------------------------------------------------

/**
 * The atlas key of the fluke mark: the card's `C_fluke` at k = 1, a crescent
 * on a short shank, filled WHITE. `fast/gfx/atlas.ts` answers it through
 * `anchorGroundSpecFor`; the recipe tints the quad to the cable ink and
 * scales it, so a fluke that grows with the share bakes nothing new (R1128).
 */
const ANCHOR_FLUKE_KEY = 'anchor:fluke';

/** The supersampling of the fluke cell: the mark is 15 px wide at k = 1 and
 *  at most twice that at a zoom under 1. */
const FLUKE_CELL_SCALE = 16;

/** The fluke path of the card at k = 1, in its own px: the crescent's outer
 *  and inner curve, and the shank. The recipe scales the whole shape by the
 *  row's `r_px` against `CRESCENT_OUT`. */
const CRESCENT_OUT = 7.5, CRESCENT_IN = 4.6, CRESCENT_Y = 0.5;
const CRESCENT_OUT_CTRL = 14, CRESCENT_IN_CTRL = 5.5;
const SHANK_HALF = 0.9, SHANK_TOP = -4, SHANK_FOOT = 5;
/** The foot of the shank line of the hollow WEIGHING fluke. */
const SHANK_HOLLOW_FOOT = 2.6;

/**
 * The one atlas cell of this file (R1128), or null for a key it does not
 * own. The cell carries the SHAPE of the card at k = 1 and no figure of a
 * row. The path is the card's `C_crescent` and its shank, filled with the
 * non-zero rule as the card fills it.
 */
export function anchorGroundSpecFor(key: string): AtlasSpec | null {
  if (key !== ANCHOR_FLUKE_KEY) return null;
  return {
    key,
    scale: FLUKE_CELL_SCALE,
    draw(ctx) {
      ctx.beginPath();
      ctx.moveTo(-CRESCENT_OUT, CRESCENT_Y);
      ctx.quadraticCurveTo(0, CRESCENT_OUT_CTRL, CRESCENT_OUT, CRESCENT_Y);
      ctx.lineTo(CRESCENT_IN, CRESCENT_Y);
      ctx.quadraticCurveTo(0, CRESCENT_IN_CTRL, -CRESCENT_IN, CRESCENT_Y);
      ctx.closePath();
      ctx.moveTo(-SHANK_HALF, SHANK_TOP);
      ctx.lineTo(-SHANK_HALF, SHANK_FOOT);
      ctx.lineTo(SHANK_HALF, SHANK_FOOT);
      ctx.lineTo(SHANK_HALF, SHANK_TOP);
      ctx.closePath();
      ctx.fillStyle = '#ffffff'; // mask
      ctx.fill();
    },
  };
}

/** How much larger the SET fluke lands, at the first moment of SET. */
const FLUKE_LAND_K = 0.35;
/** The fluke's scale at no cable out, in SETTING (it grows to 1) and in
 *  WEIGHING (it shrinks to it from 1). */
const FLUKE_SETTING_K0 = 0.45, FLUKE_WEIGHING_K0 = 0.65;
/** The SET ground stroke's half width and depth under the fluke origin, as
 *  shares of the fluke's half width (the card's 8 and 9 against 7.5). */
const GROUND_HALF_K = 8 / 7.5;
const GROUND_DEPTH_K = 9 / 7.5;

/** The lone SET bubble: its period in rise times of the slowest bubble
 *  (3.2 s against 1 / 0.22 s), the share of the period it rises, its start
 *  above the hull against where the stream starts (9 against 8), its sway
 *  against the fluke's half width (2 against 7.5) and its sway rate a unit
 *  of rise, its growth against its own radius (0.8 against 1.4), and its
 *  phase in seconds a unit of `seed`. */
const LONE_PERIOD_K = 3.2 * 0.22;
const LONE_RISE_SHARE = 0.5;
const LONE_START_K = 9 / 8;
const LONE_SWAY_K = 2 / 7.5;
const LONE_SWAY_RATE = 5;
const LONE_GROW_K = 0.8 / 1.4;
const LONE_SEED_SEC = 1.3;

/** The stream: the spread of the bubble rate against the slowest (0.2
 *  against 0.22), a bubble's radius against the lone bubble's (0.9 up to
 *  2.4 against 1.4), how much faster the WEIGHING stream runs, and the
 *  phase a unit of `seed`. */
const RATE_SPREAD_K = 0.2 / 0.22;
const BUBBLE_R0_K = 0.9 / 1.4, BUBBLE_R_SPAN_K = 1.5 / 1.4;
const WEIGH_RATE_K = 1.5;
const STREAM_SEED = 0.37;
/** The column's half width at its foot and what it adds at its top, and
 *  the wobble, as shares of the fluke's half width (1.5, 5.5 and 1.2
 *  against 7.5), and the wobble rate a unit of rise. */
const COLUMN_FOOT_K = 1.5 / 7.5, COLUMN_SPAN_K = 5.5 / 7.5;
const WOBBLE_K = 1.2 / 7.5, WOBBLE_RATE = 7;
/** A bubble keeps `1 - FADE_TOP` of its alpha at the top, and fades in over
 *  `FADE_IN` of its rise. */
const FADE_TOP = 0.7, FADE_IN = 0.06;
/** A hollow SETTING bubble grows from 0.7 to 1.3 of its radius; a solid
 *  WEIGHING dot is 0.8 of the lone radius plus 0.55 of its own. */
const RING_R0 = 0.7, RING_R_SPAN = 0.6;
const DOT_R0_K = 0.8 / 1.4, DOT_R_K = 0.55;
/** The WEIGHING tail: its gap above the dot, its length and what it adds at
 *  the top, as shares of the stream's start (1, 5 and 2 against 8). */
const TAIL_GAP_K = 1 / 8, TAIL_LEN_K = 5 / 8, TAIL_SPAN_K = 2 / 8;

/** The card's seed for the bubble table; `world.mulberry32` is the card's
 *  own generator, draw for draw. */
const BUBBLE_SEED = 5616;

/** Five draws a bubble, in the card's order: phase, rate, x, wobble phase,
 *  radius. */
const DRAWS = 5;
const bubbleDraws: number[] = [];
const nextDraw = mulberry32(BUBBLE_SEED);

/**
 * The card's `C_BUBBLES` as raw draws, each 0 to 1. The recipe turns a draw
 * into a figure of the row, so a figure change keeps the same column. The
 * table grows to the row's `count` once, on the first frame that asks.
 */
function bubbleTable(n: number): readonly number[] {
  while (bubbleDraws.length < n * DRAWS) bubbleDraws.push(nextDraw());
  return bubbleDraws;
}

/** The fluke mark with its origin at `(x, y)`, filled, `s` times its size
 *  at k = 1. */
function flukeFill(c: AnchorCtx, x: number, y: number, s: number, ink: number, a: number): void {
  const al = fillAlpha(a);
  if (s <= 0 || al <= 0) return;
  c.quads.sprite(x, y, 0, c.sprite(ANCHOR_FLUKE_KEY), ink, al, s, 0, false);
}

/**
 * A fluke mark under the hull and a column of bubbles over it, neither of
 * which turns with the heading.
 *
 * SETTING: a stream of hollow bubbles leaves the berth and drifts up; it
 * thickens with the share of cable out from `count2` bubbles to `count`, and
 * the solid fluke grows with it. SET: the stream stops, the solid fluke
 * stands on a short ground stroke, and one lone bubble leaves now and then.
 * WEIGHING: solid dots with a tail above run down toward the hull, and the
 * fluke is a hollow outline that shrinks with the share. At a zoom under 1
 * the fluke and the bubbles grow by `grow`; the column keeps its height.
 */
function bubbleColumn(c: AnchorCtx): void {
  const r = c.row, ink = c.ink, a = c.a, p = c.p;
  const g = c.z < 1 ? r.grow : 1;
  const fx = c.x, fy = c.y + r.yPx;
  const unit = r.rPx / CRESCENT_OUT;
  if (c.phase === 'set') {
    const land = 1 - easeOut(span(c.since, 0, r.flashSec));
    flukeFill(c, fx, fy, unit * g * (1 + FLUKE_LAND_K * land), ink, r.alpha * a);
    const gh = r.rPx * GROUND_HALF_K * g, gy = fy + r.rPx * GROUND_DEPTH_K * g;
    line(c, fx - gh, gy, fx + gh, gy, r.widthPx * g, ink, r.alpha3 * a);
    if (r.speed <= 0) return;
    const period = LONE_PERIOD_K / r.speed;
    const f = ((c.t + c.seed * LONE_SEED_SEC) % period) / (period * LONE_RISE_SHARE);
    if (f >= 1) return;
    ring(c, c.x + r.rPx * LONE_SWAY_K * Math.sin(f * LONE_SWAY_RATE + c.seed),
      c.y - r.length2Px * LONE_START_K - r.length3Px * f,
      r.r2Px * (1 + LONE_GROW_K * f) * g, r.width3Px * g, ink,
      r.alpha3 * a * (1 - f));
    return;
  }
  const setting = c.phase === 'setting';
  const k0 = setting ? FLUKE_SETTING_K0 : FLUKE_WEIGHING_K0;
  const s = unit * g * (k0 + (1 - k0) * p);
  if (setting) {
    flukeFill(c, fx, fy, s, ink, r.alpha * a);
  } else {
    // The card's `C_crescent` stroked, and the shank as one line.
    const w = r.width2Px * g, al = r.alpha2 * a, cy = fy + CRESCENT_Y * s;
    curve(c, fx, fy, s, -CRESCENT_OUT, CRESCENT_Y, 0, CRESCENT_OUT_CTRL,
      CRESCENT_OUT, CRESCENT_Y, w, ink, al);
    line(c, fx + CRESCENT_OUT * s, cy, fx + CRESCENT_IN * s, cy, w, ink, al);
    curve(c, fx, fy, s, CRESCENT_IN, CRESCENT_Y, 0, CRESCENT_IN_CTRL,
      -CRESCENT_IN, CRESCENT_Y, w, ink, al);
    line(c, fx - CRESCENT_IN * s, cy, fx - CRESCENT_OUT * s, cy, w, ink, al);
    line(c, fx, fy + SHANK_TOP * s, fx, fy + SHANK_HOLLOW_FOOT * s, w, ink, al);
  }
  const n = Math.min(r.count, Math.round(r.count2 + (r.count - r.count2) * p));
  const table = bubbleTable(r.count);
  const rate = setting ? 1 : WEIGH_RATE_K;
  for (let i = 0; i < n; i++) {
    const j = i * DRAWS;
    const sp = r.speed * (1 + RATE_SPREAD_K * table[j + 1]);
    const raw = (table[j] + sp * c.t * rate + c.seed * STREAM_SEED) % 1;
    const f = setting ? raw : 1 - raw;
    const ba = a * (1 - FADE_TOP * f) * span(f, 0, FADE_IN);
    if (ba <= 0) continue;
    const bx = table[j + 2] * 2 - 1, wob = table[j + 3] * TAU;
    const br = r.r2Px * (BUBBLE_R0_K + BUBBLE_R_SPAN_K * table[j + 4]);
    const x = c.x + bx * r.rPx * (COLUMN_FOOT_K + COLUMN_SPAN_K * f)
      + r.rPx * WOBBLE_K * Math.sin(wob + f * WOBBLE_RATE);
    const y = c.y - r.length2Px - r.lengthPx * f;
    if (setting) {
      ring(c, x, y, br * (RING_R0 + RING_R_SPAN * f) * g, r.width3Px * g, ink, ba);
    } else {
      disc(c, x, y, (r.r2Px * DOT_R0_K + br * DOT_R_K) * g, ink, ba);
      line(c, x, y - r.length2Px * TAIL_GAP_K,
        x, y - r.length2Px * (TAIL_LEN_K + TAIL_SPAN_K * f) * g,
        r.width3Px * g, ink, ba * r.alpha4);
    }
  }
}

// ---------------------------------------------------------------------------
// Card 20, RING TEETH
// ---------------------------------------------------------------------------

/** The backing ring's stroke, as a share of an own arc (1 against 2.5). */
const BACK_W_K = 1 / 2.5;
/** The SET ring of another faction's hull, and a SET tooth, as shares of
 *  the SET ring of an own hull (2.8 and 4.2 against 3.4). */
const SET_OTHER_W_K = 2.8 / 3.4;
const SET_TOOTH_W_K = 4.2 / 3.4;
/** A moving tooth's stroke, and the hollow dot's ring, as shares of an own
 *  arc (3 and 1.4 against 2.5). */
const TOOTH_W_K = 3 / 2.5;
const DOT_RING_W_K = 1.4 / 2.5;
/** A tooth clicks out from a hair before its quarter to a little after it. */
const TOOTH_EARLY = 0.01, TOOTH_LATE = 0.04;
/** A tooth shorter than this share of a SETTING tooth draws nothing (0.2
 *  against 5.5). */
const TOOTH_MIN_K = 0.2 / 5.5;
/** The hollow WEIGHING dot, as a share of the solid one (3.5 against 3.2). */
const DOT_HOLLOW_K = 3.5 / 3.2;
/** What the solid SETTING dot adds to the ink's alpha, as a share of an own
 *  hull's alpha (0.1 against 0.9). */
const DOT_LIFT_K = 0.1 / 0.9;

/** Tooth `q` of `count`, clockwise from twelve o'clock, `len` out from the
 *  ring, with the card's butt caps. */
function tooth(c: AnchorCtx, q: number, len: number, w: number, ink: number, a: number): void {
  if (len < c.row.length3Px * TOOTH_MIN_K) return;
  const ang = (q / c.row.count) * TAU, sx = Math.sin(ang), sy = -Math.cos(ang);
  const r0 = c.row.rPx, r1 = r0 + len;
  line(c, c.x + sx * r0, c.y + sy * r0, c.x + sx * r1, c.y + sy * r1, w, ink, a, false);
}

/**
 * The r19 ring of card 01 made into a chain wheel, with the alphas of card
 * 01.
 *
 * SETTING: the arc fills clockwise from twelve o'clock, a tooth clicks out at
 * each quarter the fill passes, and a solid dot rides the head of the arc.
 * SET: the ring closes a little thicker, and four solid teeth stand at the
 * quarters; the tooth at twelve comes with SET alone, so a cable at 95 %
 * cannot pass for SET. WEIGHING: the arc drains, each tooth folds in as the
 * drain passes it, and the head carries a hollow dot.
 */
function ringTeeth(c: AnchorCtx): void {
  const r = c.row, ink = c.ink, R = r.rPx;
  const hi = c.own ? r.alpha3 : r.alpha4;
  ring(c, c.x, c.y, R, r.widthPx * BACK_W_K, ink, c.own ? r.alpha : r.alpha2);
  if (c.phase === 'set') {
    const fl = 1 - span(c.since, 0, r.flashSec);
    ring(c, c.x, c.y, R, c.own ? r.width3Px : r.width3Px * SET_OTHER_W_K, ink, hi);
    const len = r.lengthPx + r.length2Px * fl, w = r.width3Px * SET_TOOTH_W_K;
    for (let q = 0; q < r.count; q++) tooth(c, q, len, w, ink, hi);
    return;
  }
  const p = c.p;
  arcOf(c, c.x, c.y, R, c.own ? r.widthPx : r.width2Px, TWELVE, p * TAU, ink, hi);
  const tw = r.widthPx * TOOTH_W_K;
  for (let q = 1; q < r.count; q++) {
    const at = q / r.count;
    tooth(c, q, r.length3Px * easeOut(span(p, at - TOOTH_EARLY, at + TOOTH_LATE)),
      tw, ink, hi);
  }
  const ang = p * TAU;
  const hx = c.x + Math.sin(ang) * R, hy = c.y - Math.cos(ang) * R;
  if (c.phase === 'weighing') {
    const dr = r.r2Px * DOT_HOLLOW_K;
    disc(c, hx, hy, dr, WATER_INK, 1);
    ring(c, hx, hy, dr, r.widthPx * DOT_RING_W_K, ink, hi);
  } else {
    disc(c, hx, hy, r.r2Px, ink, Math.min(1, hi + r.alpha3 * DOT_LIFT_K));
  }
}

/** The built looks of this file, by id. */
export const RECIPES: Readonly<Record<string, AnchorRecipe>> = {
  'ground-line': groundLine,
  'bubble-column': bubbleColumn,
  'ring-teeth': ringTeeth,
};

/** The looks of this file still to build. They draw the AUTO look. */
export const PENDING: readonly string[] = [];
